import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const teamMembers = [
  {
    name: 'Late Mrs. Kamna Taneja',
    initials: 'KT',
    role: 'FOUNDER',
    badge: null,
    description:
      'Founded by the visionary Kamna Taneja in 2017, she built Job Manifest from the ground up with a clear north star: make HR human again. Her leadership philosophy centers on ethical practice, deep client relationships, and the belief that the right hire changes lives.',
    email: '',
    linkedin: '#',
  },
  {
    name: 'Tanishka Taneja',
    initials: 'TT',
    role: 'DIRECTOR',
    badge: null,
    description:
      'Tanishka brings a fresh perspective to Job Manifest. With a deep-rooted connection to the company and a natural instinct for human connection, she steps into her role with energy, purpose and a modern outlook on HR.',
    email: 'tanishka@jobmanifest.in',
    linkedin: 'https://www.linkedin.com/in/tanishka-taneja-a76a30257?utm_source=share_via&utm_content=profile&utm_medium=member_ios',
  },
  {
    name: 'Tanushree Lakandri',
    initials: 'TL',
    role: 'PARTNER',
    badge: null,
    description:
      'Tanushree brings strong financial acumen to Job Manifest. She adds a sharp, numbers-driven perspective to the people business — understanding that great HR isn\'t just about culture, it\'s also about smart decisions and sustainable growth.',
    email: 'tanushree@jobmanifest.in',
    linkedin: 'https://www.linkedin.com/in/tanushree-6289061b8?utm_source=share_via&utm_content=profile&utm_medium=member_ios',
  },
];

export default function TeamSection() {
  const [revealedEmails, setRevealedEmails] = useState<Record<number, boolean>>({});
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollReveal(0.05);

  const toggleEmail = (idx: number) => {
    setRevealedEmails((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <section
      id="team"
      className="relative py-24 md:py-32 px-6 md:px-10 lg:px-16"
      style={{ backgroundColor: '#0b1f14' }}
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 md:mb-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span
            className="inline-block px-5 py-1.5 rounded-full text-xs font-bold tracking-[0.2em] uppercase mb-6 border"
            style={{ backgroundColor: '#1a3d24', color: '#c8dfc2', borderColor: '#2d5a37' }}
          >
            OUR LEADERSHIP
          </span>

          <h2
            className="font-heading text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05]"
            style={{ color: '#ffffff' }}
          >
            Meet{' '}
            <span style={{ color: '#c8dfc2' }}>Our Team</span>
          </h2>
        </div>

        {/* Cards */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
        >
          {teamMembers.map((member, idx) => (
            <div
              key={member.name}
              className={`relative rounded-2xl p-8 md:p-10 transition-all duration-500 flex flex-col border border-opacity-20 cursor-default hover:border-[#c8dfc2]/50 hover:bg-[#1a3d24] ${
                cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{
                transitionDelay: cardsVisible ? `${idx * 150}ms` : '0ms',
                backgroundColor: 'rgba(26,61,36,0.35)',
                borderColor: 'rgba(200,223,194,0.2)',
              }}
            >
              {/* Founder badge */}
              {member.badge && (
                <div className="mb-5">
                  <span
                    className="inline-block px-4 py-1.5 rounded-full text-xs font-bold"
                    style={{ backgroundColor: '#c8dfc2', color: '#1a3d24' }}
                  >
                    {member.badge}
                  </span>
                </div>
              )}

              {/* Initials circle */}
              <div
                className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center rounded-full mb-6"
                style={{ backgroundColor: '#2d5a37' }}
              >
                <span
                  className="font-heading text-2xl md:text-3xl font-extrabold"
                  style={{ color: '#c8dfc2' }}
                >
                  {member.initials}
                </span>
              </div>

              {/* Name */}
              <h3
                className="font-heading text-xl md:text-2xl font-bold mb-1 leading-tight"
                style={{ color: '#ffffff' }}
              >
                {member.name}
              </h3>

              {/* Role */}
              <p
                className="text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase mb-5"
                style={{ color: '#c8dfc2', fontFamily: 'inherit' }}
              >
                {member.role}
              </p>

              {/* Description */}
              <p
                className="text-sm leading-relaxed flex-1 text-center md:text-left"
                style={{ color: '#d4e6d0', textAlign: 'center' }}
              >
                {member.description}
              </p>

              {/* Social icons */}
              {(member.email || member.linkedin) && (
                <div
                  className="flex items-center justify-center gap-4 pt-6 mt-6 border-t"
                  style={{ borderColor: 'rgba(200,223,194,0.2)' }}
                >
                  {member.email && (
                    <div className="relative">
                      <button
                        onClick={() => toggleEmail(idx)}
                        className="flex items-center justify-center w-9 h-9 rounded-full transition-colors cursor-pointer"
                        style={{ backgroundColor: '#2d5a37', color: '#c8dfc2' }}
                        title="Reveal email"
                      >
                        <i className="ri-at-line text-sm"></i>
                      </button>
                      {revealedEmails[idx] && (
                        <span
                          className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[10px] font-medium whitespace-nowrap px-2 py-0.5 rounded"
                          style={{ backgroundColor: '#c8dfc2', color: '#1a3d24' }}
                        >
                          {member.email}
                        </span>
                      )}
                    </div>
                  )}
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center w-9 h-9 rounded-full transition-colors cursor-pointer"
                    style={{ backgroundColor: '#2d5a37', color: '#c8dfc2' }}
                    title="LinkedIn Profile"
                  >
                    <i className="ri-linkedin-fill text-sm"></i>
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}